'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';

import { DatePicker, DatePickerProps } from '../date-picker';

export interface FormDatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<DatePickerProps, 'name' | 'value' | 'onChange' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;
}

/**
 * DatePicker component integrated with React Hook Form
 * Automatically handles form registration and validation
 *
 * @example
 * ```tsx
 * <FormDatePicker
 *   name="birthDate"
 *   control={control}
 *   label="Birth Date"
 *   rules={{
 *     required: 'Birth date is required',
 *     validate: (value) => {
 *       const date = new Date(value);
 *       const now = new Date();
 *       return date < now || 'Birth date cannot be in the future';
 *     }
 *   }}
 *   max={new Date()}
 * />
 * ```
 */
export function FormDatePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, rules, showError = true, ...props }: FormDatePickerProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  return (
    <DatePicker
      {...props}
      ref={ref}
      value={value}
      onChange={(date) => {
        onChange(date);
        onBlur();
      }}
      error={showError ? error?.message : undefined}
    />
  );
}

FormDatePicker.displayName = 'FormDatePicker';
